import type { Atom } from 'jotai'
import type { NftSaleContext } from '../nftSaleMachine'

import { atom } from 'jotai'
import { loadable } from 'jotai/utils'
import { atomWithQuery } from 'jotai/query'
import * as R from 'ramda'

import formatNumber from '@/utils/formatNumber'
import { LevelEnum, RaceEnum, RACE_CHAIN_MAP, RACE_TYPE_CHAIN_MAP, LEVEL_TYPE_CHAIN_MAP } from '@/types/nftAttribute'
import { nftSaleMachineAtom } from '@/features/pwNftSale/atoms/nftSaleMachineAtom'
import { subqlEndpointAtom } from '@/atoms/subqlEndpointAtom'

import { queryPreorderCountsByRace } from '../queries'
import { SalePhases } from '../constants'

interface Sku {
  sold: number
  total: number
  preOrdered: number
}

export interface SalesData {
  price: number
  total: number
  sold: number
  preOrdered: number
  skus: Record<RaceEnum, Sku>
  open: boolean
  end: boolean
  soldout: boolean
  canPreOrder?: boolean
  summary: string
  salePhase: SalePhases
}

function makeSaleData(): SalesData {
  return {
    price: NaN,
    total: 0,
    sold: 0,
    preOrdered: 0,
    skus: {
      [RaceEnum.AISpectre]: { sold: 0, total: 0, preOrdered: 0 },
      [RaceEnum.Cyborg]: { sold: 0, total: 0, preOrdered: 0 },
      [RaceEnum.Pandroid]: { sold: 0, total: 0, preOrdered: 0 },
      [RaceEnum.XGene]: { sold: 0, total: 0, preOrdered: 0 },
    },
    open: false,
    end: false,
    soldout: false,
    summary: 'Minted 0 of 0 total',
    salePhase: SalePhases.PREPARE,
  }
}

const RaceChainSlugPairs = R.toPairs(RACE_CHAIN_MAP) as Pairs<RaceEnum, string>[]

function getCurrentdSaleData(ctx: NftSaleContext, raritySlug: string, countSold: boolean): Partial<SalesData> {
  let salesData: Partial<SalesData> & Pick<SalesData, 'skus' | 'sold' | 'total'> = {
    skus: {} as Record<RaceEnum, Sku>,
    sold: 0,
    total: 0,
  }
  salesData.salePhase = ctx.salePhase
  salesData.end = salesData.salePhase === SalePhases.CLOSED
  salesData.price = R.pathOr(0, ['prices', LEVEL_TYPE_CHAIN_MAP[raritySlug]], ctx)
  RaceChainSlugPairs.forEach((pairs) => {
    const [key, slug] = pairs
    const inventoryLevel = R.pathOr({}, ['inventoryLevel', raritySlug, slug], ctx)
    const sold = R.sum(R.values(R.pick(['raceCount', 'raceGiveawayCount', 'raceReservedCount'], inventoryLevel)))
    const total = R.sum(R.values(R.pick(['raceCount', 'raceForSaleCount', 'raceGiveawayCount', 'raceReservedCount'], inventoryLevel)))
    if (countSold || salesData.end) {
      salesData.skus[key] = R.set(R.lensProp('sold'), sold, salesData.skus[key])
      salesData.sold += sold
    }
    salesData.skus[key] = R.set(R.lensProp('total'), total, salesData.skus[key])
    salesData.total += total
  })
  return salesData
}

export const legendarySalesDataAtom = atom(get => {
  let salesData = makeSaleData()
  const snapshot = get(loadable(nftSaleMachineAtom))
  if (snapshot.state === 'hasData' && snapshot.data.context.api) {
    salesData.open = !!snapshot.data.context.canPurchaseRareOriginOfShells
    salesData = R.mergeDeepRight(salesData, getCurrentdSaleData(snapshot.data.context, 'Legendary', salesData.open))
  }
  salesData.summary = `Minted ${formatNumber(salesData.sold)} of ${formatNumber(salesData.total)} Total`
  salesData.soldout = salesData.total > 0 && salesData.sold >= salesData.total
  return salesData
})

export const magicSalesDataAtom = atom(get => {
  let salesData = makeSaleData()
  const snapshot = get(loadable(nftSaleMachineAtom))
  if (snapshot.state === 'hasData' && snapshot.data.context.api) {
    salesData.open = !!snapshot.data.context.canPurchaseRareOriginOfShells
    salesData = R.mergeDeepRight(salesData, getCurrentdSaleData(snapshot.data.context, 'Magic', salesData.open))
  }
  salesData.summary = `Minted ${formatNumber(salesData.sold)} of ${formatNumber(salesData.total)} Total`
  salesData.soldout = salesData.total > 0 && salesData.sold >= salesData.total
  return salesData
})

// create temporary atom that transfer query to loadable target.
const preorderCountsAtom = atomWithQuery(get => queryPreorderCountsByRace(get(subqlEndpointAtom)))

export const primeSalesDataAtom = atom(get => {
  let salesData = makeSaleData()

  const preorderCounts = get(loadable(preorderCountsAtom))
  if (preorderCounts.state === 'hasData') {
    const pairs = R.toPairs(RACE_TYPE_CHAIN_MAP) as Pairs<string, RaceEnum>[]
    pairs.forEach((pair) => {
      const [slug, key] = pair
      salesData.skus[key].preOrdered = preorderCounts.data[slug]
      salesData.preOrdered += preorderCounts.data[slug]
    })
  }

  const snapshot = get(loadable(nftSaleMachineAtom))
  if (snapshot.state === 'hasData' && snapshot.data.context.api) {
    salesData.open = !!snapshot.data.context.canPurchasePrimeOriginOfShells || !!snapshot.data.context.canPreorderOriginOfShells || !!snapshot.data.context.lastDayOfSale
    salesData.canPreOrder = !!snapshot.data.context.canPreorderOriginOfShells || !!snapshot.data.context.lastDayOfSale
    salesData = R.mergeDeepRight(salesData, getCurrentdSaleData(snapshot.data.context, 'Prime', salesData.open))
  }
  if (salesData.total === 0) {
    salesData.summary = ''
  } else if (salesData.canPreOrder) {
    const current = salesData.sold + salesData.preOrdered
    salesData.summary = `${formatNumber(current)} of ${formatNumber(salesData.total)} Total`
  } else {
    salesData.summary = `Minted ${formatNumber(salesData.sold)} of ${formatNumber(salesData.total)} Total`
  }
  salesData.soldout = salesData.total > 0 && salesData.sold >= salesData.total
  return salesData
})

export const salesDataAtoms: Readonly<Record<LevelEnum, Atom<SalesData>>> = {
  [LevelEnum.Common]: primeSalesDataAtom,
  [LevelEnum.Magic]: magicSalesDataAtom,
  [LevelEnum.Legendary]: legendarySalesDataAtom,
}